/**
 * Strict allowlisted rebuild for workspace capabilities payloads.
 */

export type CognitoryWorkspaceCapabilitiesView = {
  canViewQuestionAnswerActivity: boolean;
  canManageQuestionAnswerEvaluation: boolean;
  questionAnswerEvaluationFeatureAvailable: boolean;
  questionAnswerEvaluationCollectionEnabled: boolean;
};

export type CognitoryWorkspaceCapabilitiesRebuildResult =
  | {
      kind: "success";
      supportReference: string;
      cognitoryId: string;
      capabilities: CognitoryWorkspaceCapabilitiesView;
    }
  | { kind: "malformed_success"; supportReference: string | null };

const V2_ID_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
const REQUEST_ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._:-]*$/;

const CAPABILITIES_KEYS = [
  "canViewQuestionAnswerActivity",
  "canManageQuestionAnswerEvaluation",
  "questionAnswerEvaluationFeatureAvailable",
  "questionAnswerEvaluationCollectionEnabled",
] as const;

const FORBIDDEN_TREE_KEYS = new Set<string>(["canonicalUserId"]);

function isValidRequestId(value: unknown): value is string {
  return typeof value === "string" && REQUEST_ID_PATTERN.test(value);
}

function isValidCognitoryId(value: unknown): boolean {
  return typeof value === "string" && V2_ID_PATTERN.test(value);
}

function keysMatchExactly(
  record: Record<string, unknown>,
  allowed: readonly string[],
): boolean {
  const keys = Object.keys(record).sort();
  return keys.join(",") === [...allowed].sort().join(",");
}

export function treeContainsForbiddenIdentityKey(value: unknown): boolean {
  if (value === null || typeof value !== "object") {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some(item => treeContainsForbiddenIdentityKey(item));
  }
  for (const [key, nested] of Object.entries(
    value as Record<string, unknown>,
  )) {
    if (FORBIDDEN_TREE_KEYS.has(key)) {
      return true;
    }
    if (treeContainsForbiddenIdentityKey(nested)) {
      return true;
    }
  }
  return false;
}

function parseCapabilities(
  value: unknown,
): CognitoryWorkspaceCapabilitiesView | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (!keysMatchExactly(record, CAPABILITIES_KEYS)) {
    return null;
  }
  const canViewQuestionAnswerActivity = record.canViewQuestionAnswerActivity;
  const canManageQuestionAnswerEvaluation =
    record.canManageQuestionAnswerEvaluation;
  const questionAnswerEvaluationFeatureAvailable =
    record.questionAnswerEvaluationFeatureAvailable;
  const questionAnswerEvaluationCollectionEnabled =
    record.questionAnswerEvaluationCollectionEnabled;
  if (
    typeof canViewQuestionAnswerActivity !== "boolean" ||
    typeof canManageQuestionAnswerEvaluation !== "boolean" ||
    typeof questionAnswerEvaluationFeatureAvailable !== "boolean" ||
    typeof questionAnswerEvaluationCollectionEnabled !== "boolean"
  ) {
    return null;
  }
  return {
    canViewQuestionAnswerActivity,
    canManageQuestionAnswerEvaluation,
    questionAnswerEvaluationFeatureAvailable,
    questionAnswerEvaluationCollectionEnabled,
  };
}

export function rebuildCognitoryWorkspaceCapabilitiesPayload(input: {
  expectedCognitoryId: string;
  body: unknown;
}): CognitoryWorkspaceCapabilitiesRebuildResult {
  if (treeContainsForbiddenIdentityKey(input.body)) {
    return { kind: "malformed_success", supportReference: null };
  }
  if (!input.body || typeof input.body !== "object") {
    return { kind: "malformed_success", supportReference: null };
  }
  const record = input.body as Record<string, unknown>;
  const requestId = record.requestId;
  if (!isValidRequestId(requestId)) {
    return { kind: "malformed_success", supportReference: null };
  }
  if (record.decision !== "capabilities_resolved") {
    return { kind: "malformed_success", supportReference: requestId };
  }
  const cognitoryId = record.cognitoryId;
  if (
    !isValidCognitoryId(cognitoryId) ||
    cognitoryId !== input.expectedCognitoryId
  ) {
    return { kind: "malformed_success", supportReference: requestId };
  }
  const capabilities = parseCapabilities(record.capabilities);
  if (!capabilities) {
    return { kind: "malformed_success", supportReference: requestId };
  }
  return {
    kind: "success",
    supportReference: requestId,
    cognitoryId,
    capabilities,
  };
}
